import styles from "@styles/Annotation.module.css";
import rootStyle from "@styles/Root.module.css";
import { Html } from "@react-three/drei";
import { HtmlProps } from "@react-three/drei/web/Html";
import { CSSProperties, ForwardedRef, Fragment, PointerEvent, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { animated, AnimatedProps } from "@react-spring/three";
import { useFrame, Vector2 } from "@react-three/fiber";
import { Vector2 as ThreeVector2 } from "three";
import { useSpring } from "react-spring";
import { FiberUtils } from "@utils/FiberUtils";
import { useForceUpdate } from "@utils/useForceUpdate";
import useResize from "@utils/useResize";
import { useWindowEvent } from "@utils/useEvent";
import { MathUtils } from "@utils/MathUtils";

type Props = AnnotationProps & Omit<TextProps, "opacity"> & Pick<LineProps, "thickness" | "anchor">;
type AProps = AnimatedProps<Props>;
export type { AProps as AAnnotationProps };

type AnnotationProps = {
    size: number;
    dotSize: number;
    line?: boolean;
    relative?: boolean;
    opacity?: number;
    scale?: number;
    offset?: number;
    alwaysVisible?: boolean;
    color?: CSSProperties["color"];
}
export const AAnnotation = animated(({ size, dotSize, line, relative, opacity = 1, scale = 1, offset = 0, alwaysVisible, title, body, position, margin, textPosition, textStyle, titleStyle, thickness, anchor, color = "#ff7900", ...props }: HtmlProps & Props) => {
    const [hover, setHover] = useState(false);
    const [{ value: textOpacity }] = useSpring({ to: { value: hover ? 1 : 0 } }, [hover]);

    const from = useRef<HTMLDivElement>(null);
    const to = useRef<HTMLDivElement>(null);

    useWindowEvent("touchstart", e => setHover(e.target === from.current));
    const events = useMemo(() => ({
        onPointerEnter: (e: PointerEvent) => e.pointerType === "mouse" && setHover(true),
        onPointerLeave: (e: PointerEvent) => e.pointerType === "mouse" && setHover(false)
    }), [setHover]);

    const [, update] = useForceUpdate();
    useResize(update, true, [update]);

    const [scl, setScale] = useState(1);
    useFrame(({ clock }) => {
        if (scale === 1) return;
        if (hover) return;
        setScale(MathUtils.mapRanges(Math.sin(clock.elapsedTime + offset), [-1, -0.2, 0.0, 0.2, 1], [1, 1, scale, 1, 1]));
    });

    const rootStyle: CSSProperties = {
        pointerEvents: "none"
    };
    (rootStyle as any)["--size"] = `${size}rem`;
    (rootStyle as any)["--dotSize"] = `${dotSize}rem`;

    const text = <AAnnotationText fref={to} opacity={alwaysVisible ? opacity : textOpacity} {...{ margin, textPosition, title, body, textStyle, titleStyle, anchor }} />;

    if (opacity <= 0) return null;
    return (
        <>
            <Html position={position} {...props} style={rootStyle}>
                <div className={styles.topLeft}>
                    <div ref={from} className={styles.circle} style={{ opacity, pointerEvents: "initial" }} {...events}>
                        <div style={{ backgroundColor: color, pointerEvents: "none", transform: `scale(${scl})` }} />
                    </div>
                </div>
                {relative && text}
                {line && <ALine from={from.current} to={to.current} opacity={alwaysVisible ? opacity : textOpacity} {...{ thickness, anchor, color }} />}
            </Html>
            {!relative && <Html calculatePosition={() => [0, 0, 0]}>{text}</Html>}
        </>
    );
});

type TextProps = {
    margin?: number;
    textPosition?: Vector2;
    title?: string;
    body?: JSX.Element;
    opacity?: number;
    textStyle?: Omit<CSSProperties, "maxWidth" | "maxHeight" | "transform">;
    titleStyle?: CSSProperties;
}
const AAnnotationText = animated(({ margin = 20, textPosition: position, title, body, opacity = 1, textStyle, titleStyle, anchor, fref }: TextProps & Pick<LineProps, "anchor"> & { fref: ForwardedRef<HTMLDivElement> }) => {
    const x = FiberUtils.extract(position, "x");
    const y = FiberUtils.extract(position, "y");
    const textAlign = anchor === "right" ? "right" : anchor === "top" || anchor === "bottom" ? "center" : "left";

    const ref = useRef<HTMLDivElement>(null);
    useImperativeHandle(fref, () => ref.current!, []);

    const [force, update] = useForceUpdate();
    useResize(update, true);

    useEffect(() => {
        if (!ref.current) return;
        ref.current.style.maxWidth = "";
        ref.current.style.maxHeight = "";
        ref.current.style.transform = "";
        const bounds = ref.current.getBoundingClientRect();
        const outerBounds = document.documentElement.getBoundingClientRect();

        let maxWidth = bounds.width;
        let maxHeight = bounds.height;
        let translate = [0, 0];
        if (bounds.left < outerBounds.left + margin) {
            const diff = outerBounds.left + margin - bounds.left;
            maxWidth -= diff;
            translate[0] -= diff;
        }
        if (bounds.right > outerBounds.right - margin) {
            const diff = outerBounds.right - margin - bounds.right;
            maxWidth += diff;
        }
        if (bounds.top < outerBounds.top + margin) {
            const diff = outerBounds.top + margin - bounds.top;
            maxHeight -= diff;
            translate[1] -= diff;
        }
        if (bounds.bottom > outerBounds.bottom - margin) {
            const diff = outerBounds.bottom - margin - bounds.bottom;
            maxHeight += diff;
        }
        ref.current.style.maxWidth = `${maxWidth}px`;
        ref.current.style.maxHeight = `${maxHeight}px`;
        ref.current.style.transform = `translate(${translate[0]}px, ${translate[1]}px)`;
    }, [ref, force, margin]);

    return (
        <>
            <div ref={ref} className={styles.text} style={{ opacity, left: `${x}rem`, top: `${y}rem`, textAlign, ...textStyle }}>
                {title && <h2 className="accent" style={titleStyle}>{title}</h2>}
                {body}
            </div>
        </>
    );
});

type LineProps = {
    from: HTMLDivElement | null;
    to: HTMLDivElement | null;
    opacity?: number;
    thickness?: number;
    anchor?: "left" | "right" | "top" | "bottom" | Vector2;
    color?: CSSProperties["color"];
}
const ALine = animated(({ from, to, opacity = 1, thickness = 2, anchor = "left", color = "#ff7900" }: LineProps) => {
    const [force, update] = useForceUpdate();
    useResize(update, true);

    const [left, top, width, height, scale] = useMemo(() => {
        if (!from || !to) return [0, 0, 0, 0, [1, 1]];

        const fromBounds = from.getBoundingClientRect();
        const toBounds = to.getBoundingClientRect();

        const rem = parseFloat((document.querySelector(`.${rootStyle.root}`) as HTMLDivElement).style.getPropertyValue("--scale"));

        // From center
        const fromTarget = new ThreeVector2((fromBounds.left + fromBounds.right) * 0.5, (fromBounds.top + fromBounds.bottom) * 0.5);

        // To anchor
        const toTarget = new ThreeVector2(toBounds.left, toBounds.top);
        switch (anchor) {
            case "left":
                toTarget.x -= 5 * rem;
                toTarget.y += 22 * rem;
                break;
            case "right":
                toTarget.x += toBounds.width + 15 * rem;
                toTarget.y += 24 * rem;
                break;
            case "top":
                toTarget.x += toBounds.width * 0.5;
                toTarget.y -= 5 * rem;
                break;
            case "bottom":
                toTarget.x += toBounds.width * 0.5;
                toTarget.y += toBounds.height + 5 * rem;
                break;
            default:
                toTarget.x += FiberUtils.extract(anchor, "x");
                toTarget.y += FiberUtils.extract(anchor, "y");
                break;
        }

        let left = -thickness * 0.5;
        let top = -thickness * 0.5;
        let width = toTarget.x - fromTarget.x;
        let height = toTarget.y - fromTarget.y;
        let scale = [1, 1];
        if (width < 0) {
            width *= -1;
            left -= width;
            scale[0] = -1;
        }
        if (height < 0) {
            height *= -1;
            top -= height;
            scale[1] = -1;
        }
        return [left, top, width, height, scale];
    }, [force, from, to, thickness, anchor]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={styles.line} style={{
            position: "absolute",
            left: `${left - thickness * 0.5}px`,
            top: `${top - thickness * 0.5}px`,
            width: `${width + thickness}px`,
            height: `${height + thickness}px`,
            transform: `scale(${scale.join(", ")})`,
            opacity, strokeWidth: `${thickness}rem`
        }}>
            <line x1={thickness * 0.5} y1={thickness * 0.5} x2={width} y2={height} style={{ stroke: color }} />
        </svg>
    );
});